/**
 * 読者アプリの画面に出る文の台帳。管理UIの「文言」（/texts）で書き換えられる（下書き→公開。正本は Firestore の appSettings/uiTexts）。
 * 書き換えていない文は、ここの text のまま出る。{冊数} などの差し込みには、画面がその時の数を入れる。改行（\n）は改行として出す画面だけで効く。
 *
 * この台帳は local-ui/scripts/sync-shared.mjs が管理UIへ写す。だから ui-texts/*.ts の他は何も import しない（写しで読めなくなる）。
 * 作品の題・精霊や素材や館の名前・ミッションの文などのデータは、それぞれの管理画面で決めるのでここに入れない。
 * 文そのものは画面ごとに ui-texts/*.ts に置く（名前の最初の区切り＝画面。例: gacha.result.title）。
 */
import { HOME_TEXTS } from "./ui-texts/home";
import { GACHA_TEXTS } from "./ui-texts/gacha";
import { SHELF_TEXTS } from "./ui-texts/shelf";
import { READ_TEXTS } from "./ui-texts/read";
import { SPIRIT_TEXTS } from "./ui-texts/spirit";
import { TRAINING_TEXTS } from "./ui-texts/training";
import { QUESTS_TEXTS } from "./ui-texts/quests";
import { MAP_TEXTS } from "./ui-texts/map";
import { MISC_TEXTS } from "./ui-texts/misc";

export interface UiTextDef {
  /** 既定の文 */
  text: string;
  /** 管理UIに出す名前（どこの何の文か） */
  label: string;
  /** 管理UIに出す補足（いつ出るか・広い画面だけ など） */
  note?: string;
  /** 使える差し込み（{冊数} など） */
  vars?: string;
}

/** 画面ごとの見出し（台帳の名前の最初の区切りで分ける）。管理UIの「文言」はこの順に並べる */
export const UI_TEXT_SCREENS: { prefix: string; label: string }[] = [
  { prefix: "common", label: "共通" },
  { prefix: "nav", label: "下のナビ" },
  { prefix: "home", label: "ホーム" },
  { prefix: "level", label: "ホームのレベル（欄と、押すと開く板）" },
  { prefix: "gacha", label: "本を引く（ガチャ）" },
  { prefix: "shelf", label: "書庫と本" },
  { prefix: "read", label: "読書" },
  { prefix: "spirit", label: "精霊" },
  { prefix: "training", label: "精霊の育成" },
  { prefix: "quests", label: "今日の読み方・ミッション・プレゼント" },
  { prefix: "map", label: "地図" },
  { prefix: "backgrounds", label: "場所と背景を選ぶ" },
  { prefix: "encyclopedia", label: "図鑑" },
  { prefix: "settings", label: "設定" },
  { prefix: "login", label: "ログイン" },
];

export const UI_TEXTS = {
  ...HOME_TEXTS,
  ...GACHA_TEXTS,
  ...SHELF_TEXTS,
  ...READ_TEXTS,
  ...SPIRIT_TEXTS,
  ...TRAINING_TEXTS,
  ...QUESTS_TEXTS,
  ...MAP_TEXTS,
  ...MISC_TEXTS,
} as const satisfies Record<string, UiTextDef>;

export type UiTextKey = keyof typeof UI_TEXTS;
export type UiTextOverrides = Partial<Record<UiTextKey, string>>;

export const UI_TEXT_MAX = 300;

export const isUiTextKey = (k: string): k is UiTextKey => k in UI_TEXTS;

/** 文の差し込み（{冊数} など）に数を入れる。知らない差し込みはそのまま残す */
export function fillUiText(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{([^{}]+)\}/g, (all, name: string) => (name in vars ? String(vars[name]) : all));
}

/** Firestore の値を読む。知らない名前・文字でない値・長すぎる文は捨てる。空の文（""）は「空にした」として残す */
export function parseUiTexts(data: unknown): UiTextOverrides {
  const texts = (data as { texts?: Record<string, unknown> } | undefined)?.texts;
  const out: UiTextOverrides = {};
  if (!texts || typeof texts !== "object") return out;
  for (const [k, v] of Object.entries(texts)) {
    if (isUiTextKey(k) && typeof v === "string") out[k] = v.slice(0, UI_TEXT_MAX);
  }
  return out;
}
